import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, FormEvent, useCallback } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import { FormLayout, Input, Label, Larger } from '@/components/Forms';
import CurrencyField from '@/components/Forms/CurrencyField';
import { NewDropdown } from '@/components/Forms/Dropdown';
import VideoField from '@/components/Forms/Fields/VideoField';
import FormField from '@/components/Forms/FormField';
import ImageField from '@/components/Forms/ImageField';
import Note from '@/components/Forms/Note';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import {
  AddButtonHolder,
  BatchBox,
  BatchBoxHeading,
  RemoveButton,
} from '@/components/Testing';
import useAlerts from '@/hooks/useAlerts';
import useOnCloseWithConfirmation from '@/hooks/useOnCloseWithConfirmation';
import { selectTeamTestingProperties } from '@/selectors';
import { api } from '@/utils/api';
import { campaignAutomationsOptions } from '@/utils/data/testing';
import { OptimisationProjectNetworkEnum } from '@/utils/enums';
import { handleFormErrors, removeExtension } from '@/utils/helpers';
import { getAudienceSegments } from '@/utils/optimisationTests';
import { RootState } from '@/utils/store';

import AuthorField from './AuthorField';
import TestingAudienceField from './TestingAudienceField';
import TestingProjectName from './TestingProjectName';
import TestingPropertiesFields from './TestingPropertiesFields';

const StyledBatchBox = styled(BatchBox)`
  &:first-child {
    margin-top: 0;
  }
`;

const StyledNote = styled(Note)`
  margin-top: 16px;
`;

type FormDataT = {
  max_cpv: string;
  campaign_budget: string;
  title: string;
  image?: string;
  imageFile: File | null;
  author: string;
  batch: number;
  campaign_start_date: string;
  campaign_end_date: string;
  testing_audience_id?: number;
  audience_segment: string;
  campaign_automations: string;
  paste_file_name: boolean;
  multitest_enabled: boolean;
  variations: {
    name: string;
    video: string;
    initialFile?: File;
    scene_duration?: number;
  }[];
};

interface Props {
  onClose: () => void;
  network: OptimisationProjectNetworkEnum;
  projectTitle: string;
}

const TipsFormModal: FC<Props> = ({ onClose, network, projectTitle }) => {
  const { id, teamId } = useParams();

  const alerts = useAlerts();

  const userName = useSelector(
    (state: RootState) => state.auth.user?.name ?? '',
  );

  const { testingSettings, eytvTestingAudience } = useSelector(
    (state: RootState) => selectTeamTestingProperties(state, teamId!),
  );

  const queryClient = useQueryClient();

  const audienceSegments = getAudienceSegments(
    testingSettings?.google_ads_client_id ?? '',
  );

  const methods = useForm<FormDataT>({
    defaultValues: {
      max_cpv: testingSettings?.tips_max_cpv?.toString() ?? '',
      campaign_budget: testingSettings?.clips_campaign_budget?.toString() ?? '',
      paste_file_name: testingSettings?.clips_paste_file_name ?? false,
      title: '',
      batch: 1,
      author: userName,
      campaign_start_date: DateTime.now().toISODate(),
      campaign_end_date: DateTime.now().plus({ days: 1 }).toISODate(),
      imageFile: null,
      variations: [{ name: '', video: '' }],
      audience_segment: '',
      campaign_automations: campaignAutomationsOptions[0].value,
      testing_audience_id: eytvTestingAudience ?? 0,
      multitest_enabled: false,
    },
  });

  const {
    register,
    clearErrors,
    control,
    handleSubmit,
    setValue,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = methods;

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'variations',
  });

  const pasteFileName = watch('paste_file_name');

  const onCancel = useOnCloseWithConfirmation(onClose, true);

  const variationName = '{video} - {thumb}';

  const onBulkUploaded = useCallback(
    (videos: Array<{ video?: string; file: File }>, fieldsIndex?: number) => {
      videos
        .filter(({ video }) => !!video)
        .forEach(({ video, file }, index) => {
          if (index === 0) {
            return update(fieldsIndex ?? 0, {
              name: pasteFileName ? variationName : removeExtension(file.name),
              video: video!,
              initialFile: file,
            });
          }
          append({
            name: pasteFileName ? variationName : removeExtension(file.name),
            video: video!,
            initialFile: file,
          });
        });
      if (videos.filter(({ video }) => !video).length) {
        alerts.error(
          'Failed to upload some videos',
          'Those videos will be excluded from variations',
        );
      }
    },
    [append, update, alerts, pasteFileName],
  );

  const videoUploadedCallback = useCallback(
    (file: File, fieldIndex?: number) => {
      if (fieldIndex === undefined) return;

      setValue(
        `variations.${fieldIndex}.name`,
        pasteFileName ? variationName : removeExtension(file.name),
      );
      setValue(`variations.${fieldIndex}.initialFile`, file);
    },
    [setValue, pasteFileName],
  );

  const onThumbnailUploaded = (
    images?: Array<{ file: File; path?: string }>,
  ) => {
    if (!images) return;

    const [{ file, path }] = images;

    setValue('imageFile', file);
    setValue('image', path);
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async ({ variations, ...data }) => {
      try {
        const body = {
          network,
          optimisation_test_id: id!,
          title: data.title,
          batch: data.batch,
          author: data.author,
          campaign_start_date: data.campaign_start_date,
          campaign_end_date: data.campaign_end_date,
          ...(!!data.testing_audience_id && {
            testing_audience_id: data.testing_audience_id,
          }),
          multitest_enabled: data.multitest_enabled,
          audience_segment: data.audience_segment,
          campaign_automations: data.campaign_automations,
          image: data.image,
          ...(network === OptimisationProjectNetworkEnum.YOUTUBE && {
            max_cpv: data.max_cpv,
            campaign_budget: data.campaign_budget,
          }),
          variations: variations.map(
            ({ name, video, scene_duration, initialFile }) => ({
              name: name
                .replace('{video}', initialFile?.name ?? '')
                .replace('{thumb}', data.imageFile?.name ?? ''),
              video,
              scene_duration,
            }),
          ),
        };

        const { error } = await api.post<typeof body, { error?: string }>(
          '/v1/optimisation-test-units/tips',
          body,
        );

        if (error) {
          return alerts.error(error);
        }

        queryClient.invalidateQueries(['optimisation_tests'], {
          exact: false,
        });

        alerts.success(`Tips tests have been successfully created`);

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true, true);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel heading="Upload tips tests" onClose={onCancel}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <FormProvider {...methods}>
            <SidePanelContent>
              <FormLayout>
                <Larger>
                  <TestingProjectName projectTitle={projectTitle} />
                </Larger>
                {network === OptimisationProjectNetworkEnum.YOUTUBE && (
                  <>
                    <Larger>
                      <TestingPropertiesFields
                        title="Clips testing properties"
                        withoutBorder
                        hasErrors={!!errors.max_cpv || !!errors.campaign_budget}
                      >
                        <Larger>
                          <Label htmlFor="thumbsMaxCpv">
                            Clips Maximum CPV
                          </Label>
                          <CurrencyField<FormDataT>
                            control={control}
                            name="max_cpv"
                            inputId="thumbsMaxCpv"
                            placeholder="Add value"
                          />
                          {!!errors.max_cpv && (
                            <ValidationMessage>
                              {errors.max_cpv.message}
                            </ValidationMessage>
                          )}
                        </Larger>
                        <Larger>
                          <Label htmlFor="campaignBudgetInput">
                            Clips Campaign Budget
                          </Label>
                          <CurrencyField<FormDataT>
                            control={control}
                            name="campaign_budget"
                            inputId="campaignBudgetInput"
                            placeholder="Add value"
                          />
                          {!!errors.campaign_budget && (
                            <ValidationMessage>
                              {errors.campaign_budget.message}
                            </ValidationMessage>
                          )}
                        </Larger>
                        <FormField<FormDataT>
                          control={control}
                          id="pasteFileName"
                          name="paste_file_name"
                          type="switch"
                          headline="Paste File Name"
                          description="Automatically apply file name of every uploaded thumbnail/video to the variation name input field"
                        />
                      </TestingPropertiesFields>
                    </Larger>
                    <FormField<FormDataT>
                      control={control}
                      id="mutlitestSwitch"
                      name="multitest_enabled"
                      type="switch"
                      headline="Multitest"
                      description="Each video and thumbnail can be tested up to three times. Outcome will show individual results of each video as well as their average, which will provide a more accurate overall result. However, this method can cost up to three times more."
                    />
                    <TestingAudienceField<FormDataT>
                      control={control}
                      error={errors.testing_audience_id}
                    />
                    <FormField<FormDataT, string>
                      control={control}
                      label="Audience Segment"
                      type="select"
                      name="audience_segment"
                      options={audienceSegments}
                    />
                  </>
                )}
                <Larger>
                  <Label htmlFor="titleInput">Title</Label>
                  <Input
                    id="titleInput"
                    placeholder="Add title"
                    type="text"
                    $hasError={!!errors.title}
                    {...register('title')}
                  />
                  {!!errors.title && (
                    <ValidationMessage>
                      {errors.title.message}
                    </ValidationMessage>
                  )}
                </Larger>
                <Larger>
                  <ImageField
                    label="Thumbnail"
                    id="thumbnail-field"
                    pathName="image"
                    fileName="imageFile"
                    onUploaded={onThumbnailUploaded}
                  />
                </Larger>
                <Larger>
                  <Label htmlFor="batchSelect">Batch</Label>
                  <NewDropdown<number>
                    options={Array.from({ length: 11 }, (_, i) => ({
                      value: i,
                      label: `T${i}`,
                    }))}
                    control={control}
                    name="batch"
                    id="batchSelect"
                  />
                  {!!errors.batch && (
                    <ValidationMessage>
                      {errors.batch.message}
                    </ValidationMessage>
                  )}
                </Larger>
                <AuthorField<FormDataT> control={control} name="author" />
                <FormField<FormDataT>
                  control={control}
                  label="Campaign Start Date"
                  type="datepicker"
                  name="campaign_start_date"
                  datepickerType="date"
                />
                <FormField<FormDataT>
                  control={control}
                  label="Campaign End Date"
                  type="datepicker"
                  name="campaign_end_date"
                  datepickerType="date"
                />
                <FormField<FormDataT>
                  control={control}
                  label="Campaign Automations"
                  type="select"
                  options={campaignAutomationsOptions}
                  name="campaign_automations"
                />
              </FormLayout>
              {fields.map((item, index) => (
                <StyledBatchBox key={item.id}>
                  <BatchBoxHeading $type="button">
                    Tips variation #{index + 1}
                    {index > 0 && (
                      <RemoveButton
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          remove(index);
                        }}
                      >
                        Remove
                      </RemoveButton>
                    )}
                  </BatchBoxHeading>
                  <FormLayout>
                    <Larger>
                      <Label htmlFor="tipNameInput">Tip name</Label>
                      <Input
                        id="tipNameInput"
                        placeholder="Add name"
                        $hasError={!!errors.variations?.[index]?.name}
                        type="text"
                        {...register(`variations.${index}.name`)}
                      />
                      {!!errors.variations?.[index]?.name && (
                        <ValidationMessage>
                          {errors.variations?.[index]?.name?.message}
                        </ValidationMessage>
                      )}
                    </Larger>
                    <Larger>
                      <FormField<FormDataT>
                        control={control}
                        name={`variations.${index}.scene_duration`}
                        label="Scene Duration"
                        type="duration"
                      />
                      <StyledNote title="Note">
                        If you don’t add scene duration manually, duration
                        length will be applied automatically from the uploaded
                        video file.
                      </StyledNote>
                    </Larger>
                    <Larger>
                      <Label>Videos</Label>
                      <VideoField<FormDataT>
                        name={`variations.${index}.video`}
                        control={control}
                        fieldsIndex={index}
                        onBulkUploaded={onBulkUploaded}
                        initialFile={item.initialFile}
                        videoUploadedCallback={videoUploadedCallback}
                        notes={
                          <>
                            Format: MP4
                            <br />
                            Max file size: 5GB
                            <br />
                            Upload multiple files to create multiple variations
                          </>
                        }
                      />
                    </Larger>
                  </FormLayout>
                </StyledBatchBox>
              ))}
              <AddButtonHolder>
                <Button
                  type="button"
                  $type="colorized"
                  onClick={() => append({ name: '', video: '' })}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 2V14"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2 8H14"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Add variation
                </Button>
              </AddButtonHolder>
            </SidePanelContent>
          </FormProvider>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              Upload
            </Button>
            <Button type="button" color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default TipsFormModal;
